import { Autocomplete, InputLabel, Stack, TextField, Typography } from '@mui/material'
import { Controller, useFormContext } from 'react-hook-form'
import { useEditMode } from 'contexts/edit-mode.context'
import { OrderEventCompanySuggestion } from 'store/orders/orders.types'
import {
  EDITABLE_VALUE_COLOR,
  READ_ONLY_VALUE_COLOR,
  readOnlyAutocompleteSx,
  readOnlyTextFieldSx,
} from '../field-read-only.styles'

type OrderEventCompanyNameFieldProps = {
  eventIndex: number
  label: string
  suggestions?: OrderEventCompanySuggestion[]
  suggestionsLoading?: boolean
  highlight?: boolean
}

const OrderEventCompanyNameField = ({
  eventIndex,
  label,
  suggestions = [],
  suggestionsLoading = false,
  highlight = false,
}: OrderEventCompanyNameFieldProps) => {
  const { control, setValue } = useFormContext()
  const { isEditMode, hasContext } = useEditMode()
  const isReadOnly = hasContext && !isEditMode
  const valueColor = isReadOnly ? READ_ONLY_VALUE_COLOR : EDITABLE_VALUE_COLOR
  const showHighlight = Boolean(highlight) && !isReadOnly
  const nameField = `orderEvents.${eventIndex}.name`
  const addressField = `orderEvents.${eventIndex}.address`

  const clearAddress = () => {
    setValue(addressField, '', { shouldDirty: true })
  }

  const applySuggestion = (suggestion: OrderEventCompanySuggestion) => {
    setValue(addressField, suggestion.address, { shouldDirty: true })
  }

  return (
    <Stack spacing={1}>
      <InputLabel
        sx={
          showHighlight
            ? { color: 'error.main', fontWeight: 700 }
            : isReadOnly
              ? { color: 'text.secondary', fontWeight: 500, fontSize: '0.875rem' }
              : undefined
        }
      >
        {label}
      </InputLabel>
      <Controller
        name={nameField}
        control={control}
        render={({ field: { onChange, value }, fieldState: { error } }) => (
          <Autocomplete
            freeSolo
            disabled={isReadOnly}
            disableClearable={isReadOnly}
            options={isReadOnly ? [] : suggestions}
            loading={!isReadOnly && suggestionsLoading}
            value={null}
            inputValue={value || ''}
            sx={isReadOnly ? readOnlyAutocompleteSx : undefined}
            onInputChange={(_, inputValue, reason) => {
              if (isReadOnly || reason === 'reset') return
              onChange(inputValue)
              if (reason === 'clear' || inputValue === '') {
                clearAddress()
                return
              }
              if (reason === 'input') {
                clearAddress()
              }
            }}
            onChange={(_, option) => {
              if (isReadOnly) return
              if (!option || typeof option === 'string') {
                onChange(typeof option === 'string' ? option : '')
                if (!option) {
                  clearAddress()
                }
                return
              }
              onChange(option.name)
              applySuggestion(option)
            }}
            getOptionLabel={(option) =>
              typeof option === 'string' ? option : option.name
            }
            isOptionEqualToValue={(option, selectedValue) =>
              option.id === selectedValue.id
            }
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                <Stack spacing={0.25}>
                  <Typography variant="body2">{option.name}</Typography>
                  {option.address ? (
                    <Typography variant="caption" color="text.secondary">
                      {option.address}
                    </Typography>
                  ) : null}
                </Stack>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                error={!!error}
                helperText={error?.message}
                inputProps={{
                  ...params.inputProps,
                  autoComplete: 'off',
                  readOnly: isReadOnly,
                }}
                sx={
                  isReadOnly
                    ? readOnlyTextFieldSx
                    : {
                        '& .MuiInputBase-input': {
                          color: valueColor,
                        },
                        '& .MuiInputBase-input.Mui-disabled': {
                          WebkitTextFillColor: valueColor,
                          color: valueColor,
                        },
                      }
                }
              />
            )}
          />
        )}
      />
    </Stack>
  )
}

export default OrderEventCompanyNameField
